import { Section } from '@advanza/advanza_generic'
import { Icon } from '@advanza/ui'
import TableList from 'components/TableList'
import FormattedTime from 'components/ui/FormattedTime'
import ReviewListContainer from 'containers/ReviewListContainer'
import React from 'react'
import { Link } from 'react-router-dom'
import ReviewEntityWrapped from './ReviewEntity'
import ReviewStars from './ReviewStars'

/** Container injects entity from entityId; type for passing entityId. */
const ReviewEntity = ReviewEntityWrapped as React.ComponentType<{ entityId: string }>

const REVIEW_STATUS_ICON: Record<
    number,
    {
        name: string
        red?: boolean
        green?: boolean
        orange?: boolean
        color?: string
        style?: React.CSSProperties
    }
> = {
    0: { name: 'deleted', red: true, color: 'red' },
    1: { name: 'done', green: true, color: '#4BB545' },
    2: { name: 'thumb_down', style: { color: '#555' }, color: '#555' },
    3: { name: 'schedule', orange: true, color: '#f46511' },
}

interface ReviewUser {
    first_name: string
    last_name: string
}

interface ReviewEntityShape {
    user?: ReviewUser
    name?: string
    status: number
    rating: number
    created: string
    requestId?: number
    userEmail?: string
    _open?: boolean
}

interface ReviewEntities {
    reviews: Record<string, ReviewEntityShape>
}

export interface ReviewListProps {
    filter?: Record<string, unknown>
    pages?: Record<number, string[]>
    reviewEntities: ReviewEntities
    isFetching?: boolean
    select?: (ids: (string | number)[], filterId: string) => void
    compact?: boolean
    hideHeader?: boolean
    selectable?: boolean
    filterId?: string
    changeFilter?: (opts: Record<string, unknown>) => void
    count?: number
    url?: string
    rows?: Record<string, unknown>
    showCols?: string[]
    addListFeatures?: boolean
    hideSearch?: boolean
    hidePagination?: boolean
    hideExport?: boolean
    hideOrder?: boolean
    showServicesSelect?: boolean
    error?: boolean
}

function ReviewList(props: ReviewListProps) {
    const { selectable, reviewEntities, ...rest } = props
    const { reviews } = reviewEntities

    const definitions = {
        id: { name: '#' },
        name: { name: 'name' },
        stars: { name: 'rating' },
        time: { name: 'time' },
        reviewLink: { name: 'reviewLink' },
        userEmail: { name: 'userEmail' },
    }

    const renderColumns = (id: string): React.ReactNode[] => {
        const review = reviews[id]
        if (!review) return []
        const name = review.user
            ? `${review.user.first_name} ${review.user.last_name}`
            : (review.name ?? 'anonymous')
        const deleted = review.status === 0
        const statusIcon = REVIEW_STATUS_ICON[review.status ?? 0] ?? REVIEW_STATUS_ICON[3]

        return [
            <small key="id" className="c-grey">
                #{id}
            </small>,
            <span
                key="name"
                className="ellipsis"
                style={{ maxWidth: '9em', color: statusIcon.color }}>
                <Icon {...statusIcon} /> {deleted ? <s>{name}</s> : name}
            </span>,
            <ReviewStars key="stars" className="ellipsis" avg={review.rating / 2} showOnlyStars />,
            <small key="time" className="c-grey">
                <FormattedTime date={review.created} format="d MMM yyyy" />
            </small>,
            review.requestId ? (
                <Link
                    key="link"
                    className="plain"
                    to={`/service/requests/request/${review.requestId}`}>
                    <Icon>link</Icon> Request
                </Link>
            ) : (
                <React.Fragment key="link" />
            ),
            review.userEmail ? (
                <div key="email">{review.userEmail}</div>
            ) : (
                <React.Fragment key="email" />
            ),
        ]
    }

    const renderRow = (id: string) => {
        const review = reviews[id]
        return {
            cols: renderColumns(id),
            expandOnClick: true,
            expandable: (
                <Section x y>
                    <ReviewEntity entityId={id} />
                </Section>
            ),
            expanded: review?._open,
            id,
        }
    }

    return (
        <TableList
            {...rest}
            selectable={selectable}
            renderRow={renderRow}
            definitions={definitions}
        />
    )
}

export default ReviewListContainer(ReviewList)
